import React, { useCallback, useEffect, useState } from "react";
import { Spinner, Alert, Nav, NavItem, NavLink, Input } from "reactstrap";
import { sequenceApi } from "../../../../services/sequenceServices";
import { usersApi } from "../../../../services/userServices";
import DraggableSequenceSection from "./DraggableSequenceSection";

const STATUS_SECTIONS = [
  { statusId: 2, label: "New Jobs" },
  { statusId: 3, label: "Detailing Jobs" },
  { statusId: 4, label: "Waiting for Check" },
  { statusId: 5, label: "Checking Jobs" },
  { statusId: 8, label: "Waiting for Scrubbing" },
  { statusId: 9, label: "Scrubbing Jobs" },
  { statusId: 10, label: "Waiting for Look Over" },
  { statusId: 11, label: "Look Over Jobs" },
];

const PAGE_SIZE = 500;
const DETAILER_CHECKER_ROLE_ID = 4;

const getPersonKey = (person) => `${person.id}`;

const getPersonLabel = (person) => `${person.firstName} ${person.lastName}`;

const sortByPriority = (sequences) =>
  [...sequences].sort((a, b) => {
    const pa = a.priority ?? Number.MAX_SAFE_INTEGER;
    const pb = b.priority ?? Number.MAX_SAFE_INTEGER;
    return pa - pb;
  });

const ManagerDashboard = () => {
  const [people, setPeople] = useState([]);
  const [selectedPersonKey, setSelectedPersonKey] = useState("");
  const [personSequences, setPersonSequences] = useState([]);
  const [loadingPeople, setLoadingPeople] = useState(true);
  const [loadingSequences, setLoadingSequences] = useState(false);
  const [error, setError] = useState(null);
  const [reloadCounter, setReloadCounter] = useState(0);

  // On mount: fetch all users with the Detailer/Checker role
  useEffect(() => {
    const loadPeople = async () => {
      try {
        setLoadingPeople(true);
        setError(null);
        const result = await usersApi.getUsers({ roleId: DETAILER_CHECKER_ROLE_ID, pageSize: 1000 });
        const users = (result.data || []).sort((a, b) =>
          `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)
        );
        setPeople(users);
        if (users.length > 0) {
          setSelectedPersonKey(getPersonKey(users[0]));
        }
      } catch {
        setError("Failed to load team members. Please try again.");
      } finally {
        setLoadingPeople(false);
      }
    };
    loadPeople();
  }, []);

  // Whenever the selected person changes, fetch their sequences from the API
  useEffect(() => {
    if (!selectedPersonKey) return;

    const loadPersonSequences = async () => {
      try {
        setLoadingSequences(true);
        const result = await sequenceApi.getAllSequences({
          pageSize: PAGE_SIZE,
          sortBy: "priority",
          direction: "asc",
          detailerId: selectedPersonKey,
          checkerId: selectedPersonKey,
        });
        setPersonSequences(result.data || []);
      } catch {
        setError("Failed to load sequences. Please try again.");
      } finally {
        setLoadingSequences(false);
      }
    };

    loadPersonSequences();
  }, [selectedPersonKey, reloadCounter]);

  const handleReorder = useCallback((reorderedSection) => {
    const reorderedIds = new Set(reorderedSection.map((s) => s.id));
    const withUpdatedPriorities = reorderedSection.map((seq, idx) => ({
      ...seq,
      priority: idx + 1,
    }));
    setPersonSequences((prev) => [
      ...prev.filter((s) => !reorderedIds.has(s.id)),
      ...withUpdatedPriorities,
    ]);
  }, []);

  const handleStatusChange = useCallback(() => {
    setReloadCounter((c) => c + 1);
  }, []);

  if (loadingPeople) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <Spinner color="primary" />
        <span className="ml-2">Loading queues...</span>
      </div>
    );
  }

  if (error) {
    return <Alert color="danger">{error}</Alert>;
  }

  if (people.length === 0) {
    return (
      <Alert color="info">
        No active detailers or checkers found in the system.
      </Alert>
    );
  }

  return (
    <div>
      <Nav tabs className="d-flex text-center flex-nowrap job-details-nav mt-3">
        <NavItem>
          <NavLink active className="d-flex align-items-center py-2 px-3">
            <Input
              type="select"
              bsSize="sm"
              value={selectedPersonKey}
              onChange={(e) => setSelectedPersonKey(e.target.value)}
              style={{ minWidth: 220, cursor: "pointer" }}
              className="border-0 bg-transparent font-weight-bold font-size-95 p-0"
            >
              {people.map((person) => (
                <option key={getPersonKey(person)} value={getPersonKey(person)}>
                  {getPersonLabel(person)}
                </option>
              ))}
            </Input>
          </NavLink>
        </NavItem>
      </Nav>

      {loadingSequences ? (
        <div className="d-flex justify-content-center align-items-center py-4">
          <Spinner color="primary" size="sm" />
          <span className="ml-2 text-muted">Loading sequences...</span>
        </div>
      ) : (
        STATUS_SECTIONS.map(({ statusId, label }) => {
          const sectionSequences = sortByPriority(
            // eslint-disable-next-line eqeqeq
            personSequences.filter((s) => s.sequenceStatusId == statusId),
          );
          return (
            <DraggableSequenceSection
              key={statusId}
              title={label}
              sequences={sectionSequences}
              onReorder={handleReorder}
              onStatusChange={handleStatusChange}
            />
          );
        })
      )}
    </div>
  );
};

export default ManagerDashboard;
